import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Text,
  VStack
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import { zodResolver } from '@hookform/resolvers/zod';
import { Step, Steps, useSteps } from 'chakra-ui-steps';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import tw from 'twin.macro';

import { createFetcher } from '../../lib/fetcher';
import { CreateReportFormData, createReportSchema } from '../../models/reports/input';
import { useReportStore } from '../../providers';
import { Card } from '../Card';
import { GrowthRates } from '../GrowthRates';
import { useAutosaveAndRestoreForm } from '../hooks';
import { Dollar, Percent } from '../icons';
import { PropertyExpenses } from '../PropertyExpenses';
import { PropertyLocation } from '../PropertyLocation';
import { PurchaseInformation } from '../PurchaseInformation';
import { PropertyRevenue } from '../RevenueAndOccupancy';

export const Section = styled.div`
  ${tw`m-4`}
`;

const Container = styled(Card)`
  ${tw`m-10 px-4`}
`;

export const IconWrapper = styled.div`
  ${tw`flex relative justify-center items-center`}
`;

export const IconRight = styled.span`
  ${tw`flex absolute right-0`};
`;

export const IconLeft = styled.span`
  ${tw`flex absolute left-0`};
`;

export const DollarIcon = styled(Dollar)`
  ${tw`pr-1.5`}
  height: 1.75rem;
  width: 1.75rem;
  padding-bottom: 0.15rem;
`;

export const PercentIcon = styled(Percent)`
  ${tw`pr-1.5`}
  height: 1.75rem;
  width: 1.75rem;
`;

export const ReportContent = styled.pre`
  background: #eaeaea;
  padding: 1rem;
  font-size: 8pt;
  font-family: monospace;
`;

export function PropertyInformation() {
  const _form = useForm<CreateReportFormData>({
    mode: 'all',
    resolver: zodResolver(createReportSchema)
  });
  const { handleSubmit } = _form;
  const { updateLocation, updatePurchase, updateGrowthRates, updateRevenue, updateExpenses } =
    useReportStore();
  const navigate = useNavigate();

  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // not meant for production, but useful when developing
  useAutosaveAndRestoreForm('purchase-information-form-data', _form, {
    active: process.env.NODE_ENV === 'development'
  });

  const onSubmit = useCallback(
    async (data: CreateReportFormData) => {
      setIsLoading(true);
      const { street, unit, city, state, zipCode } = data;
      updateLocation({ street, unit, city, state, zipCode });

      const {
        purchasePrice,
        downPayment,
        interestRate,
        term,
        closingCosts,
        rehabCosts,
        salesCommission,
        isRehab,
        arv,
        taxes,
        insurance,
        pmi,
        furnishings
      } = data;
      updatePurchase({
        purchasePrice,
        downPayment,
        interestRate,
        term,
        closingCosts,
        rehabCosts,
        salesCommission,
        isRehab,
        arv,
        taxes,
        insurance,
        pmi,
        furnishings
      });

      const { appreciation, revenueGrowthRate, expenseGrowthRate } = data;
      updateGrowthRates({ appreciation, revenueGrowthRate, expenseGrowthRate });

      const { nightlyRevenue, occupancy } = data;
      updateRevenue({ nightlyRevenue, occupancy });

      const {
        electricity,
        internet,
        utilities,
        gas,
        hoaFees,
        maintenance,
        capitalExpenditures,
        managementFees,
        bookingPlatformFees,
        cleaningFees,
        cleansPerMonth,
        landscaping,
        snowRemoval,
        pestControl,
        legalFees,
        permitAndLicenseFees
      } = data;
      updateExpenses({
        electricity,
        internet,
        utilities,
        gas,
        hoaFees,
        maintenance,
        capitalExpenditures,
        managementFees,
        bookingPlatformFees,
        cleaningFees,
        cleansPerMonth,
        landscaping,
        snowRemoval,
        pestControl,
        legalFees,
        permitAndLicenseFees
      });

      const result = await createReportFetcher(data);

      if (result.ok) {
        navigate(`/report/${result.result.id}`);
      } else {
        setError(result.error);
        setIsLoading(false);
      }
    },
    [navigate, updateExpenses, updateLocation, updateGrowthRates, updatePurchase, updateRevenue]
  );

  const { nextStep, prevStep, activeStep } = useSteps({
    initialStep: 0
  });
  const steps = [0, 1, 2, 3];

  return (
    <FormProvider {..._form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container>
          <Box m="4">
            <VStack align="start" mb="10">
              <Heading>Welcome to needle!</Heading>
              <Text>
                Fill out the form below to receive a detailed analysis of your potential short-term
                rental!
              </Text>
            </VStack>
            <Steps orientation="vertical" activeStep={activeStep} colorScheme="green">
              <Step width="100%" label={'Property Location'} key={'Property Location'}>
                <PropertyLocation />
              </Step>
              <Step width="100%" label={'Purchase Information'} key={'Purchase Information'}>
                <PurchaseInformation />
              </Step>
              <Step width="100%" label={'Revenue and Occupancy'} key={'Revenue and Occupancy'}>
                <PropertyRevenue />
              </Step>
              <Step width="100%" label={'Property Expenses'} key={'Property Expenses'}>
                <PropertyExpenses />
              </Step>
              <Step width="100%" label={'Growth Rates'} key={'Growth Rates'}>
                <GrowthRates />
              </Step>
            </Steps>
            {activeStep === steps.length ? (
              <HStack px={4} py={4} width="100%" flexDirection="row" justify="flex-end">
                <Button isDisabled={activeStep === 0} onClick={prevStep} size="sm">
                  Prev
                </Button>
                <Button
                  isDisabled={activeStep !== 4}
                  type="submit"
                  mx="2"
                  size="sm"
                  variant="solid"
                  isLoading={isLoading}
                >
                  Submit
                </Button>
              </HStack>
            ) : (
              <Flex width="100%" justify="flex-end">
                <Button
                  isDisabled={activeStep === 0}
                  mr={4}
                  onClick={prevStep}
                  size="sm"
                  variant="outline"
                >
                  Prev
                </Button>
                <Button size="sm" onClick={nextStep} variant="solid">
                  Next
                </Button>
              </Flex>
            )}
            {error && (
              <Alert status="error">
                <AlertIcon />
                {error}
              </Alert>
            )}
          </Box>
        </Container>
      </form>
    </FormProvider>
  );
}

export default PropertyInformation;

const createReportFetcher = createFetcher<CreateReportFormData, { id: string }>({
  baseUrl: '/api/reports',
  method: 'POST'
});
